import { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { Close, Inventory2Outlined } from "@mui/icons-material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

import { useModal } from "../../../hooks/useModal";
import { postShipmentProfilesApi } from "./setupApi";
import { SHIPMENT_PROFILES_KEY } from "./ShipmentProfiles";
import { flattenApiErrors, getErrorMessage } from "../orders/orderUtils";

export interface ProfileShipmentData {
  order_channel: string;
  product_name: string;
  description: string;
  length: number;
  width: number;
  height: number;
  weight: number;
  hsn_code: string;

  [key: string]: string | number;
}

type FormState = Record<"order_channel" | "product_name" | "description" | "length" | "width" | "height" | "weight" | "hsn_code", string>;

const EMPTY: FormState = {
  order_channel: "",
  product_name: "",
  description: "",
  length: "",
  width: "",
  height: "",
  weight: "",
  hsn_code: "",
};

// Channel used when the field is left blank (the old form always sent this value).
const DEFAULT_CHANNEL = "Channel";

const positive = (v: string) => {
  const n = Number(v);
  return v.trim() !== "" && Number.isFinite(n) && n > 0;
};

const validate = (f: FormState) => {
  const e: Partial<Record<keyof FormState, string>> = {};
  if (!f.product_name.trim()) e.product_name = "Enter the product name";
  else if (f.product_name.length > 50) e.product_name = "Keep it under 50 characters";
  if (!f.description.trim()) e.description = "Describe what's in the package";
  else if (f.description.length > 150) e.description = "Keep it under 150 characters";
  if (!f.hsn_code.trim()) e.hsn_code = "Enter the HSN code";
  else if (!/^\d{4,8}$/.test(f.hsn_code.trim())) e.hsn_code = "HSN codes are 4 to 8 digits";
  (["length", "width", "height"] as const).forEach((k) => {
    if (!positive(f[k])) e[k] = "Required";
  });
  if (!positive(f.weight)) e.weight = "Enter a weight above 0";
  return e;
};

const AddProfileModal = () => {
  const { isOpen, type, onClose } = useModal();
  const open = isOpen && type === "profile";
  const fullScreen = useMediaQuery("(max-width:599px)");
  const queryClient = useQueryClient();

  const [form, setForm] = useState<FormState>(EMPTY);
  const [touched, setTouched] = useState<Partial<Record<keyof FormState, boolean>>>({});
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState("");

  const errors = validate(form);
  const isValid = Object.keys(errors).length === 0;
  const show = (k: keyof FormState) => (touched[k] || submitted) && errors[k];

  const close = () => {
    if (save.isPending) return;
    setForm(EMPTY);
    setTouched({});
    setSubmitted(false);
    setServerError("");
    onClose();
  };

  const save = useMutation({
    mutationFn: () =>
      postShipmentProfilesApi({
        order_channel: form.order_channel.trim() || DEFAULT_CHANNEL,
        product_name: form.product_name.trim(),
        description: form.description.trim(),
        hsn_code: form.hsn_code.trim(),
        length: Number(form.length),
        width: Number(form.width),
        height: Number(form.height),
        weight: Number(form.weight),
      }),
    onSuccess: () => {
      toast.success(`Profile "${form.product_name.trim()}" saved`);
      queryClient.invalidateQueries({ queryKey: SHIPMENT_PROFILES_KEY });
      close();
    },
    onError: (error: any) => {
      const issues = flattenApiErrors(error?.response?.data);
      setServerError(
        issues.length
          ? issues.map((i) => (i.field ? `${i.field}: ${i.message}` : i.message)).join(" · ")
          : getErrorMessage(error, "Couldn't save the profile. Please try again.")
      );
    },
  });

  const submit = () => {
    setSubmitted(true);
    setServerError("");
    if (isValid) save.mutate();
  };

  const field = (
    k: keyof FormState,
    label: string,
    props: { placeholder?: string; unit?: string; numeric?: boolean; multiline?: boolean; helper?: string } = {}
  ) => (
    <TextField
      fullWidth
      size="small"
      label={label}
      value={form[k]}
      placeholder={props.placeholder}
      multiline={props.multiline}
      minRows={props.multiline ? 2 : undefined}
      onChange={(e) => {
        const v = e.target.value;
        if (props.numeric && v !== "" && !/^\d*\.?\d*$/.test(v)) return;
        setForm((f) => ({ ...f, [k]: v }));
      }}
      onBlur={() => setTouched((t) => ({ ...t, [k]: true }))}
      error={!!show(k)}
      helperText={show(k) || props.helper || " "}
      inputProps={props.numeric ? { inputMode: "decimal" } : undefined}
      InputProps={props.unit ? { endAdornment: <InputAdornment position="end">{props.unit}</InputAdornment> } : undefined}
    />
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={close}
      PaperProps={{ sx: { width: fullScreen ? "100%" : 460, display: "flex", flexDirection: "column" } }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 3, py: 2 }}>
        <Stack direction="row" alignItems="center" gap={1.5}>
          <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: "#eef4ff", color: "#1f7aec", display: "grid", placeItems: "center" }}>
            <Inventory2Outlined fontSize="small" />
          </Box>
          <Box>
            <Typography fontWeight={700} fontSize={18}>
              New shipment profile
            </Typography>
            <Typography fontSize={12} color="text.secondary">
              Reuse it whenever you ship this product.
            </Typography>
          </Box>
        </Stack>
        <IconButton onClick={close} disabled={save.isPending} aria-label="Close">
          <Close />
        </IconButton>
      </Stack>
      <Divider />

      <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 2.5 }}>
        <Typography fontSize={13} fontWeight={700} color="text.secondary" mb={1.5}>
          PRODUCT
        </Typography>
        <Stack gap={0.5}>
          {field("product_name", "Product name", { placeholder: "e.g. Cotton T-shirt" })}
          {field("description", "Description", { placeholder: "What's inside the package", multiline: true })}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              {field("hsn_code", "HSN code", { placeholder: "e.g. 6109", numeric: true })}
            </Grid>
            <Grid item xs={6}>
              {field("order_channel", "Channel", { placeholder: DEFAULT_CHANNEL, helper: "Optional" })}
            </Grid>
          </Grid>
        </Stack>

        <Typography fontSize={13} fontWeight={700} color="text.secondary" mt={1.5} mb={1.5}>
          PACKAGE
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={4}>
            {field("length", "Length", { unit: "cm", numeric: true })}
          </Grid>
          <Grid item xs={4}>
            {field("width", "Width", { unit: "cm", numeric: true })}
          </Grid>
          <Grid item xs={4}>
            {field("height", "Height", { unit: "cm", numeric: true })}
          </Grid>
          <Grid item xs={6}>
            {field("weight", "Weight", { unit: "kg", numeric: true })}
          </Grid>
        </Grid>

        {serverError && (
          <Typography fontSize={13} color="error" mt={1}>
            {serverError}
          </Typography>
        )}
      </Box>

      <Divider />
      <Stack direction="row" justifyContent="flex-end" gap={1} sx={{ px: 3, py: 2 }}>
        <Button onClick={close} disabled={save.isPending} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={submit}
          disabled={save.isPending || (submitted && !isValid)}
          startIcon={save.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600, px: 3 }}
        >
          {save.isPending ? "Saving…" : "Save profile"}
        </Button>
      </Stack>
    </Drawer>
  );
};

export default AddProfileModal;
